import { SCOPE3_CATEGORIES_META } from '../types/ghg-settings';

export interface Scope3CategoryRouteMeta {
  number: number;
  label: string;
  slug: string;
}

export interface Scope3CategoryDetailMeta extends Scope3CategoryRouteMeta {
  summary: string;
  include: string[];
  typicalActivities: string[];
  dataHints: string[];
  evidenceHints: string[];
  outOfScope: string[];
  defaultActivityMethod: string;
  quickTemplates: Scope3CategoryQuickTemplate[];
}

export interface Scope3CategoryQuickTemplate {
  id: string;
  label: string;
  description: string;
  activityMethod: string;
  methodValues: Record<string, string>;
  methodUnits: Record<string, string>;
  activityDescription?: string | undefined;
  notes?: string | undefined;
}

const SCOPE3_CATEGORY_SLUG_BY_NUMBER: Record<number, string> = {
  1: 'purchased-goods-services',
  2: 'capital-goods',
  3: 'fuel-and-energy-activities',
  4: 'upstream-transportation-distribution',
  5: 'waste-generated-in-operations',
  6: 'business-travel',
  7: 'employee-commuting',
  8: 'upstream-leased-assets',
  9: 'downstream-transportation-distribution',
  10: 'processing-of-sold-products',
  11: 'use-of-sold-products',
  12: 'end-of-life-treatment-of-sold-products',
  13: 'downstream-leased-assets',
  14: 'franchises',
  15: 'investments',
};

export const SCOPE3_CATEGORY_ROUTES: Scope3CategoryRouteMeta[] = SCOPE3_CATEGORIES_META.map(
  (category) => ({
    number: category.number,
    label: category.name,
    slug: SCOPE3_CATEGORY_SLUG_BY_NUMBER[category.number] ?? `category-${String(category.number)}`,
  })
);

export const SCOPE3_CATEGORY_BY_NUMBER = new Map(
  SCOPE3_CATEGORY_ROUTES.map((category) => [category.number, category])
);

export const SCOPE3_CATEGORY_BY_SLUG = new Map(
  SCOPE3_CATEGORY_ROUTES.map((category) => [category.slug, category])
);

const SCOPE3_CATEGORY_DEFAULT_DETAIL = {
  summary: 'Track indirect emissions from upstream and downstream value-chain activities.',
  include: ['Activity data that materially contributes to this category total.'],
  typicalActivities: ['Category-specific procurement, logistics, use, or disposal records.'],
  dataHints: ['Capture quantity, unit, facility/vendor context, and reporting period.'],
  evidenceHints: ['Invoices, contracts, utility records, supplier disclosures, and manifests.'],
  outOfScope: ['Direct Scope 1 and purchased-energy Scope 2 activities.'],
  defaultActivityMethod: 'Number',
  quickTemplates: [],
} satisfies Omit<Scope3CategoryDetailMeta, 'number' | 'label' | 'slug'>;

const SCOPE3_CATEGORY_QUICK_TEMPLATES: Record<number, Scope3CategoryQuickTemplate[]> = {
  1: [
    {
      id: 'supplier-spend',
      label: 'Supplier Spend',
      description: 'Use supplier spend as an initial purchased-goods estimate.',
      activityMethod: 'Money',
      methodValues: { money: '10000' },
      methodUnits: { money_unit: 'usd' },
      activityDescription: 'Purchased goods and services spend estimate.',
      notes: 'Template: supplier spend estimate for Category 1.',
    },
  ],
  2: [
    {
      id: 'capex-spend',
      label: 'Capex Spend',
      description: 'Use capex value as a starting point for capital goods.',
      activityMethod: 'Money',
      methodValues: { money: '50000' },
      methodUnits: { money_unit: 'usd' },
      activityDescription: 'Capital goods spend estimate.',
      notes: 'Template: capex spend estimate for Category 2.',
    },
  ],
  3: [
    {
      id: 'fuel-energy',
      label: 'Energy Consumption',
      description: 'Capture upstream fuel and energy activity in kWh.',
      activityMethod: 'Energy',
      methodValues: { energy: '50000' },
      methodUnits: { energy_unit: 'kWh' },
      activityDescription: 'Fuel and energy-related upstream activity.',
      notes: 'Template: upstream fuel and energy estimate for Category 3.',
    },
  ],
  4: [
    {
      id: 'inbound-freight',
      label: 'Inbound Freight Lane',
      description: 'Estimate upstream freight by weight and lane distance.',
      activityMethod: 'WeightOverDistance',
      methodValues: { weight: '1000', distance: '250' },
      methodUnits: { weight_unit: 'kg', distance_unit: 'km' },
      activityDescription: 'Upstream transportation lane estimate.',
      notes: 'Template: inbound freight estimate for Category 4.',
    },
  ],
  5: [
    {
      id: 'waste-stream',
      label: 'Waste Stream',
      description: 'Track waste mass by treatment pathway.',
      activityMethod: 'Weight',
      methodValues: { weight: '500' },
      methodUnits: { weight_unit: 'kg' },
      activityDescription: 'Waste generated in operations estimate.',
      notes: 'Template: operational waste estimate for Category 5.',
    },
  ],
  6: [
    {
      id: 'business-flight',
      label: 'Business Travel Leg',
      description: 'Capture a representative business-trip distance.',
      activityMethod: 'Distance',
      methodValues: { distance: '1200' },
      methodUnits: { distance_unit: 'km' },
      activityDescription: 'Business travel distance estimate.',
      notes: 'Template: business travel estimate for Category 6.',
    },
  ],
  7: [
    {
      id: 'employee-commute',
      label: 'Commuting Distance',
      description: 'Capture a representative commuting distance value.',
      activityMethod: 'Distance',
      methodValues: { distance: '25' },
      methodUnits: { distance_unit: 'km' },
      activityDescription: 'Employee commuting distance estimate.',
      notes: 'Template: commuting estimate for Category 7.',
    },
  ],
  8: [
    {
      id: 'leased-floor-area',
      label: 'Leased Floor Area',
      description: 'Estimate upstream leased asset activity from floor area.',
      activityMethod: 'Area',
      methodValues: { area: '2000' },
      methodUnits: { area_unit: 'm2' },
      activityDescription: 'Upstream leased asset floor area estimate.',
      notes: 'Template: upstream leased assets estimate for Category 8.',
    },
  ],
  9: [
    {
      id: 'outbound-freight',
      label: 'Outbound Freight Lane',
      description: 'Estimate downstream freight by weight and distance.',
      activityMethod: 'WeightOverDistance',
      methodValues: { weight: '1000', distance: '400' },
      methodUnits: { weight_unit: 'kg', distance_unit: 'km' },
      activityDescription: 'Downstream transportation lane estimate.',
      notes: 'Template: downstream freight estimate for Category 9.',
    },
  ],
  10: [
    {
      id: 'processing-throughput',
      label: 'Processing Throughput',
      description: 'Estimate processing impacts by mass throughput.',
      activityMethod: 'Weight',
      methodValues: { weight: '5000' },
      methodUnits: { weight_unit: 'kg' },
      activityDescription: 'Processing of sold products throughput estimate.',
      notes: 'Template: processing estimate for Category 10.',
    },
  ],
  11: [
    {
      id: 'use-phase-energy',
      label: 'Use-Phase Energy',
      description: 'Estimate sold-product use-phase electricity/fuel demand.',
      activityMethod: 'Energy',
      methodValues: { energy: '150000' },
      methodUnits: { energy_unit: 'kWh' },
      activityDescription: 'Use of sold products energy estimate.',
      notes: 'Template: use-phase estimate for Category 11.',
    },
  ],
  12: [
    {
      id: 'end-of-life-mass',
      label: 'End-of-Life Mass',
      description: 'Estimate disposal impacts by sold-product mass.',
      activityMethod: 'Weight',
      methodValues: { weight: '2000' },
      methodUnits: { weight_unit: 'kg' },
      activityDescription: 'End-of-life treatment mass estimate.',
      notes: 'Template: end-of-life estimate for Category 12.',
    },
  ],
  13: [
    {
      id: 'downstream-leased-area',
      label: 'Leased Asset Area',
      description: 'Estimate downstream leased asset activity using area.',
      activityMethod: 'Area',
      methodValues: { area: '10000' },
      methodUnits: { area_unit: 'm2' },
      activityDescription: 'Downstream leased assets floor area estimate.',
      notes: 'Template: downstream leased assets estimate for Category 13.',
    },
  ],
  14: [
    {
      id: 'franchise-sites',
      label: 'Franchise Site Count',
      description: 'Start with a site-count estimate for franchise operations.',
      activityMethod: 'Number',
      methodValues: { number: '25' },
      methodUnits: {},
      activityDescription: 'Franchise operations estimate by site count.',
      notes: 'Template: franchise operations estimate for Category 14.',
    },
  ],
  15: [
    {
      id: 'portfolio-exposure',
      label: 'Portfolio Exposure',
      description: 'Use financed exposure amount as a starting estimate.',
      activityMethod: 'Money',
      methodValues: { money: '1000000' },
      methodUnits: { money_unit: 'usd' },
      activityDescription: 'Investment portfolio exposure estimate.',
      notes: 'Template: financed emissions estimate for Category 15.',
    },
  ],
};

function quickTemplatesFor(categoryNumber: number): Scope3CategoryQuickTemplate[] {
  return SCOPE3_CATEGORY_QUICK_TEMPLATES[categoryNumber] ?? [];
}

const SCOPE3_CATEGORY_DEFAULT_METHOD_BY_NUMBER: Record<number, string> = {
  1: 'Money',
  2: 'Money',
  3: 'Energy',
  4: 'WeightOverDistance',
  5: 'Weight',
  6: 'Distance',
  7: 'Distance',
  8: 'Area',
  9: 'WeightOverDistance',
  10: 'Weight',
  11: 'Energy',
  12: 'Weight',
  13: 'Area',
  14: 'Number',
  15: 'Money',
};

const SCOPE3_CATEGORY_DETAIL_CONTENT: Record<
  number,
  Omit<Scope3CategoryDetailMeta, 'number' | 'label' | 'slug'>
> = {
  1: {
    summary: 'Purchased goods and services from suppliers used in your operations.',
    include: [
      'Raw materials, consumables, packaging, and purchased services.',
      'Supplier-specific data where available; spend-based estimates as fallback.',
    ],
    typicalActivities: ['Paper, chemicals, components, IT services, outsourced processing.'],
    dataHints: ['Supplier name, quantity or spend, unit, category, and geography.'],
    evidenceHints: ['Purchase orders, invoices, ERP spend exports, supplier factors.'],
    outOfScope: ['Capitalized assets (Category 2) and fuel/energy well-to-tank impacts (Category 3).'],
    defaultActivityMethod: 'Money',
    quickTemplates: quickTemplatesFor(1),
  },
  2: {
    summary: 'Emissions from production of capital goods purchased by the organization.',
    include: [
      'Machinery, equipment, buildings, and major long-lived assets.',
      'One-time project purchases and construction-related capital inputs.',
    ],
    typicalActivities: ['Manufacturing line equipment, fleet purchases, construction materials.'],
    dataHints: ['Capex category, supplier, mass/spend basis, commissioning date.'],
    evidenceHints: ['Fixed asset register, capex approvals, procurement contracts.'],
    outOfScope: ['Routine operational purchases classified as Category 1.'],
    defaultActivityMethod: 'Money',
    quickTemplates: quickTemplatesFor(2),
  },
  3: {
    summary: 'Fuel- and energy-related activities not already included in Scope 1 or Scope 2.',
    include: [
      'Upstream extraction, production, and transport of purchased fuels/energy.',
      'Transmission and distribution losses for purchased electricity.',
    ],
    typicalActivities: ['Well-to-tank fuel impacts, electricity T&D losses, upstream steam production.'],
    dataHints: ['Energy carrier, purchased quantity, region/country, loss factor source.'],
    evidenceHints: ['Fuel invoices, utility bills, energy management exports.'],
    outOfScope: ['Direct combustion emissions (Scope 1) and purchased electricity use (Scope 2).'],
    defaultActivityMethod: 'Energy',
    quickTemplates: quickTemplatesFor(3),
  },
  4: {
    summary: 'Third-party upstream transportation and distribution of purchased products.',
    include: [
      'Inbound freight, warehousing, and distribution before products reach your operations.',
      'Contracted logistics providers and parcel shipments.',
    ],
    typicalActivities: ['Ocean freight, trucking inbound goods, third-party storage.'],
    dataHints: ['Distance, mode, mass/volume, shipment count, lane geography.'],
    evidenceHints: ['Bill of lading, carrier reports, freight invoices, TMS exports.'],
    outOfScope: ['Company-owned transport (Scope 1) and downstream deliveries (Category 9).'],
    defaultActivityMethod: 'WeightOverDistance',
    quickTemplates: quickTemplatesFor(4),
  },
  5: {
    summary: 'Treatment and disposal of waste generated in operations.',
    include: [
      'Landfill, incineration, recycling, composting, and wastewater treatment.',
      'Hazardous and non-hazardous waste streams.',
    ],
    typicalActivities: ['Municipal solid waste, process waste, sludge, treatment pathways.'],
    dataHints: ['Waste type, treatment method, mass, moisture/content assumptions.'],
    evidenceHints: ['Waste manifests, hauler reports, weighbridge tickets, vendor statements.'],
    outOfScope: ['End-of-life treatment of sold products (Category 12).'],
    defaultActivityMethod: 'Weight',
    quickTemplates: quickTemplatesFor(5),
  },
  6: {
    summary: 'Business travel emissions from employee trips not owned/operated by your company.',
    include: [
      'Air, rail, hotel, and rented/third-party ground transportation.',
      'Both booked and reimbursed business travel activities.',
    ],
    typicalActivities: ['Flights, train trips, hotel nights, taxi and ride-share mileage.'],
    dataHints: ['Trip leg distance, class of travel, nights stayed, region.'],
    evidenceHints: ['Travel agency exports, expense reports, corporate card data.'],
    outOfScope: ['Employee commuting (Category 7) and company-owned vehicles (Scope 1).'],
    defaultActivityMethod: 'Distance',
    quickTemplates: quickTemplatesFor(6),
  },
  7: {
    summary: 'Employee commuting between home and work locations.',
    include: [
      'Daily commute patterns by transport mode and frequency.',
      'Remote/hybrid work assumptions when relevant.',
    ],
    typicalActivities: ['Car commute, public transit, cycling/walking share, remote work days.'],
    dataHints: ['Mode split, distance bands, attendance frequency, employee population coverage.'],
    evidenceHints: ['Commuting surveys, HR roster, office attendance telemetry.'],
    outOfScope: ['Business travel (Category 6).'],
    defaultActivityMethod: 'Distance',
    quickTemplates: quickTemplatesFor(7),
  },
  8: {
    summary: 'Upstream leased assets not already included in Scope 1 or Scope 2.',
    include: [
      'Leased facilities, vehicles, or equipment where lessor-owned operations apply.',
      'Assets used by your organization but not directly controlled for Scopes 1/2.',
    ],
    typicalActivities: ['Leased office space, outsourced data centers, leased machinery.'],
    dataHints: ['Lease term, area/capacity, energy intensity, allocation approach.'],
    evidenceHints: ['Lease agreements, landlord utility statements, submeter data.'],
    outOfScope: ['Assets already included in your Scope 1/2 inventories.'],
    defaultActivityMethod: 'Area',
    quickTemplates: quickTemplatesFor(8),
  },
  9: {
    summary: 'Downstream transportation and distribution after products leave your control.',
    include: [
      'Third-party outbound freight and distribution centers.',
      'Storage and handling in downstream logistics chain.',
    ],
    typicalActivities: ['Customer delivery freight, regional distribution, export logistics.'],
    dataHints: ['Mode, route distance, shipment mass, fulfillment node.'],
    evidenceHints: ['Carrier files, distribution contracts, logistics invoices.'],
    outOfScope: ['Inbound transport (Category 4).'],
    defaultActivityMethod: 'WeightOverDistance',
    quickTemplates: quickTemplatesFor(9),
  },
  10: {
    summary: 'Processing of intermediate sold products by downstream entities.',
    include: [
      'Energy/emissions from further processing required before final product use.',
      'Downstream manufacturing or transformation steps.',
    ],
    typicalActivities: ['Refining, blending, assembly, finishing by customers/processors.'],
    dataHints: ['Downstream process assumptions, throughput, process yield/loss factors.'],
    evidenceHints: ['Product lifecycle studies, customer process data, industry datasets.'],
    outOfScope: ['Direct use-phase emissions (Category 11).'],
    defaultActivityMethod: 'Weight',
    quickTemplates: quickTemplatesFor(10),
  },
  11: {
    summary: 'Use-phase emissions from sold products over their lifetime.',
    include: [
      'Energy-consuming products and direct-emitting products while in use.',
      'Assumed use patterns, lifetime, and regional energy mixes.',
    ],
    typicalActivities: ['Appliance electricity use, fuel consumed by sold equipment.'],
    dataHints: ['Lifetime use profile, annual operating hours, efficiency assumptions.'],
    evidenceHints: ['Product specs, usage surveys, market data, lifecycle assumptions.'],
    outOfScope: ['End-of-life disposal and treatment impacts (Category 12).'],
    defaultActivityMethod: 'Energy',
    quickTemplates: quickTemplatesFor(11),
  },
  12: {
    summary: 'End-of-life treatment of sold products and packaging.',
    include: [
      'Waste treatment pathways after customer use (landfill, recycling, incineration).',
      'Expected recovery and disposal rates by market.',
    ],
    typicalActivities: ['Product disposal, packaging recycling rates, treatment scenario modeling.'],
    dataHints: ['Material composition, disposal mix, geography, recovery assumptions.'],
    evidenceHints: ['Packaging declarations, product BOM, waste statistics datasets.'],
    outOfScope: ['Operational waste generated by your own sites (Category 5).'],
    defaultActivityMethod: 'Weight',
    quickTemplates: quickTemplatesFor(12),
  },
  13: {
    summary: 'Downstream leased assets where your company acts as lessor.',
    include: [
      'Assets owned by your company and leased to others, if not in Scopes 1/2.',
      'Energy and operational impacts during lease period.',
    ],
    typicalActivities: ['Leased buildings/equipment portfolio energy use estimates.'],
    dataHints: ['Asset inventory, lease occupancy/utilization, allocation assumptions.'],
    evidenceHints: ['Lease portfolio records, tenant utility data, metering reports.'],
    outOfScope: ['Upstream leased assets used by your own operations (Category 8).'],
    defaultActivityMethod: 'Area',
    quickTemplates: quickTemplatesFor(13),
  },
  14: {
    summary: 'Emissions from franchise operations not included in Scopes 1/2.',
    include: [
      'Franchisee energy, fuel, and operational emissions per franchise model.',
      'Representative data scaled across franchise network.',
    ],
    typicalActivities: ['Franchise site electricity, fuel use, and waste operations.'],
    dataHints: ['Store count, floor area, throughput, regional intensity assumptions.'],
    evidenceHints: ['Franchise disclosures, audited reports, utility extracts, surveys.'],
    outOfScope: ['Company-owned locations already included in Scopes 1/2.'],
    defaultActivityMethod: 'Number',
    quickTemplates: quickTemplatesFor(14),
  },
  15: {
    summary: 'Investment-related emissions from financed or managed assets.',
    include: [
      'Equity/debt investments, project finance, and other financed activities.',
      'Attribution using ownership, exposure, or PCAF-aligned methods.',
    ],
    typicalActivities: ['Portfolio financed emissions, managed funds, lending book allocation.'],
    dataHints: ['Exposure amount, attribution factor, investee emissions quality rating.'],
    evidenceHints: ['Portfolio holdings, financial statements, disclosed emissions datasets.'],
    outOfScope: ['Direct emissions from your own operational footprint.'],
    defaultActivityMethod: 'Money',
    quickTemplates: quickTemplatesFor(15),
  },
};

export const SCOPE3_CATEGORY_DETAILS: Scope3CategoryDetailMeta[] = SCOPE3_CATEGORY_ROUTES.map(
  (category) => ({
    ...category,
    ...SCOPE3_CATEGORY_DEFAULT_DETAIL,
    defaultActivityMethod:
      SCOPE3_CATEGORY_DEFAULT_METHOD_BY_NUMBER[category.number] ??
      SCOPE3_CATEGORY_DEFAULT_DETAIL.defaultActivityMethod,
    quickTemplates: SCOPE3_CATEGORY_QUICK_TEMPLATES[category.number] ?? [],
    ...(SCOPE3_CATEGORY_DETAIL_CONTENT[category.number] ?? {}),
  })
);

export const SCOPE3_CATEGORY_DETAIL_BY_NUMBER = new Map(
  SCOPE3_CATEGORY_DETAILS.map((category) => [category.number, category])
);

export function scope3CategoryPath(slug: string): string {
  return `/ghg/scope3/${slug}`;
}

export function getScope3CategoryNeighbors(categoryNumber: number): {
  previous?: Scope3CategoryRouteMeta | undefined;
  next?: Scope3CategoryRouteMeta | undefined;
} {
  const index = SCOPE3_CATEGORY_ROUTES.findIndex((category) => category.number === categoryNumber);
  if (index < 0) {
    return {};
  }
  return {
    previous: index > 0 ? SCOPE3_CATEGORY_ROUTES[index - 1] : undefined,
    next: index < SCOPE3_CATEGORY_ROUTES.length - 1 ? SCOPE3_CATEGORY_ROUTES[index + 1] : undefined,
  };
}
